/**
 * Splitting one written name into first + last — ONE definition, used
 * everywhere a list of people is sorted or exported.
 *
 * WHY THIS EXISTS. Every person's name in this system is captured as a single
 * box ("Parent/Guardian Name", "Child's name"), because that is the shortest
 * form for a parent filling the form on a phone. A single box is fine to
 * READ and hopeless to SORT: "Ada Jones" and "Adam Zhang" file under A, and a
 * host looking for a family in a class list of ninety goes hunting.
 *
 * So the split is DERIVED, here, rather than asked for twice on the form —
 * which also means it applies to every registration already on record, not
 * only to the ones taken after today.
 *
 * It is deliberately PURE (strings in, strings out) so the Worker's spreadsheet
 * feed, the admin tables and the CSV download all sort identically. A name that
 * sorts one way in the portal and another way in the linked sheet is worse than
 * no sorting at all.
 *
 * WHAT IT CANNOT DO. No rule can be right about every name in the world — a
 * two-word given name, a two-word family name with no particle, a name written
 * family-name-first without a comma. The rules below cover the shapes that
 * actually turn up, and the FULL name is always kept beside the split columns,
 * so nothing is lost when the guess is wrong.
 */

/** A written name, split into the two parts a list can be sorted by. */
export interface PersonNameParts {
  /** Given name(s) — everything before the family name. "" if unknown. */
  first: string;
  /** Family name, including any particle ("van der Berg"). "" for a
   *  single-word name, where there is nothing to call a surname. */
  last: string;
}

/**
 * Words that belong WITH the family name rather than the given names, so
 * "Maria van der Berg" files under V and not under B.
 *
 * Lower case, compared case-insensitively. Deliberately short: every entry is
 * a word that can ONLY be a particle in a two-or-more word name, so it can
 * never swallow a real given name.
 */
const SURNAME_PARTICLES = new Set([
  "van",
  "von",
  "de",
  "del",
  "della",
  "der",
  "den",
  "di",
  "da",
  "das",
  "dos",
  "du",
  "la",
  "le",
  "lo",
  "ter",
  "ten",
  "bin",
  "binti",
  "binte",
  "ibn",
  "al",
  "st",
]);

const normalisedWord = (word: string): string => word.toLowerCase().replace(/\.$/, "");

/**
 * Split a written name into first and last.
 *
 * The rules, in order:
 *  1. Blank in, blank out — a missing name is not an error here.
 *  2. "Smith, John" (the spreadsheet convention) splits on the comma, family
 *     name first. Someone pasting a list from another system gets it right.
 *  3. Otherwise the LAST word is the family name, with any particles in front
 *     of it pulled along ("van der Berg").
 *  4. A single word stays as the first name, with no surname invented for it.
 */
export function splitPersonName(raw: string | null | undefined): PersonNameParts {
  const text = String(raw ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return { first: "", last: "" };

  const comma = text.indexOf(",");
  if (comma > 0) {
    const last = text.slice(0, comma).trim();
    const first = text.slice(comma + 1).replace(/,/g, " ").replace(/\s+/g, " ").trim();
    if (last && first) return { first, last };
  }

  const words = text.split(" ");
  if (words.length === 1) return { first: text, last: "" };

  // Walk back over particles, but never past the first word: a name has to
  // keep at least one given name, or "De Sousa" would have no first name.
  let start = words.length - 1;
  while (start > 1 && SURNAME_PARTICLES.has(normalisedWord(words[start - 1] ?? ""))) start--;

  return { first: words.slice(0, start).join(" "), last: words.slice(start).join(" ") };
}

/** Just the family name, for a "sort by surname" column. */
export function personLastName(raw: string | null | undefined): string {
  return splitPersonName(raw).last;
}

/** Just the given name(s). */
export function personFirstName(raw: string | null | undefined): string {
  return splitPersonName(raw).first;
}

/**
 * The value a "by surname" sort compares — family name first, then given name
 * as the tie-break, so the three Nguyens sit together in a sensible order.
 *
 * A single-word name has no surname to file under, so it sorts on itself
 * rather than sinking to the top of the list as an empty string.
 */
export function personSortKey(raw: string | null | undefined): string {
  const { first, last } = splitPersonName(raw);
  return (last ? `${last} ${first}` : first).trim().toLowerCase();
}

/** Compare two written names by family name. Pass straight to `.sort()`. */
export function compareByLastName(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  return personSortKey(a).localeCompare(personSortKey(b));
}

/** Compare two written names by given name (what "A–Z" meant before this). */
export function compareByFirstName(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  return String(a ?? "")
    .trim()
    .toLowerCase()
    .localeCompare(String(b ?? "").trim().toLowerCase());
}
